import { useState } from 'react';
import type { ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { BrandLogo } from './BrandLogo';
import { AppTour, useTour } from '../tour/AppTour';
import { TourProvider } from '../../contexts/TourContext';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { showTour, closeTour, openTour } = useTour(user?.id);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <TourProvider value={showTour}>
    <div className="min-h-screen bg-background text-on-background">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
        onOpenTour={openTour}
      />

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-surface-container-low border-b border-outline-variant/15 flex items-center px-4 gap-3">
        <button
          onClick={() => setSidebarOpen(true)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors"
          aria-label="Open menu"
        >
          <span className="material-symbols-outlined block text-[22px] leading-none">menu</span>
        </button>
        <BrandLogo compact />
      </div>

      <main className={`min-h-screen pt-14 md:pt-0 transition-all duration-300 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        {children}
      </main>

      {showTour && <AppTour onClose={closeTour} />}
    </div>
    </TourProvider>
  );
}
